/**
 * TableToolbarButton — a single icon button inside a table toolbar.
 *
 * Matches the v3 design spec: 32×32 square, 8px radius, transparent
 * ground, muted (`text-3`) icon, swaps to the brand colour when
 * `active` or during hover. One visual primitive so every table
 * ends up with an identical toolbar regardless of which icons it
 * picks.
 *
 * Intentionally dumb: the consumer hands over `icon` (any ReactNode
 * — MUI, Tabler, raw SVG) and the click handler, nothing else. MRT
 * wiring, filter state, modals, etc. all live higher up.
 */
import { forwardRef } from 'react';
import { styled } from '@mui/material/styles';

const Button = styled('button', {
  shouldForwardProp: prop => prop !== '$active',
})(({ $active }) => ({
  // Relative anchor so the tooltip pseudo-element can position
  // itself under the button.
  position: 'relative',
  width: 32,
  height: 32,
  borderRadius: 8,
  background: $active ? 'var(--qhs-surface-hover)' : 'transparent',
  color: $active ? 'var(--qhs-brand)' : 'var(--qhs-text-3)',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 120ms ease, color 120ms ease',
  // Normalise icon sizing so MUI / Tabler / raw svg all render at 16px
  // without each consumer having to size them individually.
  '& svg': { width: 16, height: 16 },
  '&:hover': {
    background: 'var(--qhs-surface-hover)',
    color: $active ? 'var(--qhs-brand)' : 'var(--qhs-text-1)',
  },
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.4,
    background: 'transparent',
    color: 'var(--qhs-text-4)',
  },
  '&:focus-visible': {
    outline: '2px solid var(--qhs-brand)',
    outlineOffset: 1,
  },
  // Custom tooltip — styled via ::after so every toolbar button
  // gets a consistent, brand-aware hint without pulling in MUI's
  // Tooltip portal. The native `title` attribute is removed on the
  // element itself to avoid the double-tooltip flash; the label is
  // read from `data-tooltip` instead.
  '&[data-tooltip]::after': {
    content: 'attr(data-tooltip)',
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '4px 8px',
    background: 'var(--qhs-text-1)',
    color: 'var(--qhs-surface)',
    fontSize: 11,
    fontWeight: 500,
    borderRadius: 4,
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 120ms ease 350ms, visibility 0s linear 470ms',
    zIndex: 100,
    boxShadow: 'var(--qhs-sh-2)',
  },
  '&[data-tooltip]:hover::after, &[data-tooltip]:focus-visible::after': {
    opacity: 1,
    visibility: 'visible',
    transition: 'opacity 120ms ease 350ms, visibility 0s linear 350ms',
  },
  // Disabled buttons keep their tooltip so a permission-gated action
  // can explain why it is unavailable on hover.
}));

// `forwardRef` so Popover (and any other downstream wrapper) can
// anchor to the underlying <button>. Required by `<Popover trigger=…>`,
// which clones the trigger and injects a ref to measure its rect.
const TableToolbarButton = forwardRef(
  (
    { icon, title, active = false, disabled = false, onClick, 'aria-label': ariaLabel, ...rest },
    ref
  ) => (
    <Button
      ref={ref}
      type="button"
      // `data-tooltip` drives the custom pseudo-element tooltip; the
      // native `title` attribute is intentionally omitted to avoid the
      // OS-level tooltip flashing over ours on slower pointers.
      data-tooltip={title || undefined}
      aria-label={ariaLabel || title}
      aria-pressed={active || undefined}
      onClick={onClick}
      disabled={disabled}
      $active={active}
      {...rest}
    >
      {icon}
    </Button>
  )
);

export default TableToolbarButton;
